import { EventIds } from 'constants/eventIds';
import { log } from 'utils';

// TODO: Fine tune to prevent lambda timeout
const PRERENDER_TIMEOUT = 10000;

// export for testing
export class PrerenderManager {
    private isPrerendering: boolean | undefined;
    private componentsRequired: string[];
    private componentsReady: string[];
    private timeoutId: number | undefined;

    constructor() {
        this.isPrerendering = window.prerenderCloudIsServerSideRendering;
        this.componentsRequired = [];
        this.componentsReady = [];

        if (this.isPrerendering) {
            window.prerendercloudReady = false;
            this.timeoutFallback();
        } else {
            log.warn(
                '[PrerenderManager] intialize PrerenderManager while prerenderCloudIsServerSideRendering is false',
                EventIds.PrerenderManagerInitializedWhenNotPrerendering,
            );
        }
    }

    public destroy(): void {
        this.cancelTimeoutFallback();
    }

    public cancelTimeoutFallback(): void {
        clearTimeout(this.timeoutId);
    }

    private timeoutFallback(): void {
        this.timeoutId = window.setTimeout(() => {
            if (!window.prerendercloudReady) {
                log.warn(
                    '[PrerenderManager] Manually set prerendercloudReady to true, to prevent timeout',
                    EventIds.ManualySetPrerendercloudReadyToTrue,
                );
                window.prerendercloudReady = true;
            }
        }, PRERENDER_TIMEOUT);
    }

    public updateRequiredComponents(componentNames: string[]): void {
        if (!this.isPrerendering) return;

        // add the new components to the existing ones
        this.componentsRequired = [
            ...this.componentsRequired,
            ...componentNames,
        ];
    }

    public componentDoneRendering(componentName: string): void {
        if (!this.isPrerendering) return;

        if (this.componentsReady.includes(componentName)) {
            log.warn(
                '[PrerenderManager] Each entry of "componentDoneRendering" must be unique',
                EventIds.PrerenderDuplicateComponentName,
            );
        }

        this.componentsReady.push(componentName);
        this.checkRenderingStatus();
    }

    private checkRenderingStatus(): void {
        const prerendercloudIsReady = window.prerendercloudReady;
        const hasNoComponentsRequired = this.componentsRequired.length === 0;
        const hasNoReadyComponents = this.componentsReady.length === 0;

        if (
            !this.isPrerendering ||
            prerendercloudIsReady ||
            hasNoComponentsRequired ||
            hasNoReadyComponents
        ) {
            return;
        }

        const hasUnmonitoredComponents = this.componentsReady.every(
            (component) => !this.componentsRequired.includes(component),
        );

        if (hasUnmonitoredComponents) {
            log.warn(
                '[PrerenderManager] Not all components done rendering were monitored',
                EventIds.PrerenderUnmonitoredComponentDoneRendering,
                undefined,
                {
                    componentsReady: this.componentsReady,
                },
            );
        }

        const allMonitoredComponentsHaveRendered =
            this.componentsRequired.every((component) =>
                this.componentsReady.includes(component),
            );

        if (allMonitoredComponentsHaveRendered) {
            this.markPageHasDoneRendering();
        }
    }

    private markPageHasDoneRendering(): void {
        if (!this.isPrerendering) return;

        if (window.prerendercloudReady) {
            log.warn(
                '[PrerenderManager] "markPageHasDoneRendering" have been called more than once, this should not happen',
                EventIds.PrerenderPageHasDoneRenderingTwice,
            );
            return;
        }

        window.prerendercloudReady = true;

        this.cancelTimeoutFallback();

        log.info(
            '[PrerenderManager] Page has been marked has done rendering',
            EventIds.PrerenderPageHasDoneRendering,
        );
    }
}

const prerenderManager = window.prerenderCloudIsServerSideRendering
    ? new PrerenderManager()
    : null;

export default prerenderManager;
